import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Card, Button, Badge, AsyncBoundary, Drawer, useToast, EmptyState,
} from '@kollez/design-system';
import { useExams, useCreateExam, usePublishExam, type ExamSectionInput } from '@/lib/queries';
import { useAuthStore } from '@/lib/authStore';

const REVIEWER_ROLES = new Set([
  'tpo', 'principal', 'vice_principal',
  'group_admin', 'group_manager',
  'university_admin', 'university_registrar',
]);

/**
 * /assess/exams (F67) — list assessments for the tenant + create-exam drawer.
 * Take/results flow lives on /assess/take/:examId.
 */
export function AssessExamsPage() {
  const list = useExams();
  const publish = usePublishExam();
  const toast = useToast();
  const [openNew, setOpenNew] = useState(false);
  const role = useAuthStore((s) => s.user?.role ?? '');
  const canFilterByPartner = REVIEWER_ROLES.has(role);

  async function doPublish(id: string) {
    try {
      await publish.mutateAsync(id);
      toast.show({ message: 'Saved as ready. Attach to an opportunity workflow to open the window.' });
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message ?? 'Publish failed';
      toast.show({ tone: 'warning', message: msg });
    }
  }

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Assessments</p>
          <h1 className="h-page">Assessments &amp; tests</h1>
          <p className="subhead">Build a test, publish it, and share the take link with candidates.</p>
        </div>
        <div className="ph-actions">
          <Button variant="primary" onClick={() => setOpenNew(true)}>+ New assessment</Button>
        </div>
      </div>

      {canFilterByPartner && (
        <div className="flex items-center gap-2" style={{ paddingLeft: 2 }}>
          <span className="chip" style={{ opacity: 0.55, cursor: 'not-allowed' }} aria-disabled="true" title="Coming soon">
            By partner (coming soon)
          </span>
          <span className="helper" style={{ fontSize: 11, color: 'rgb(var(--ink-tertiary))' }}>
            Filter partner-authored assessments once the server-side filter lands.
          </span>
        </div>
      )}

      <AsyncBoundary
        kind="table" loading={list.isLoading} error={list.error}
        empty={!list.isLoading && !list.data?.assessments.length}
        emptyTitle="No assessments yet"
        emptyHint="Create your first assessment — mix fixed questions with random draws from a bank."
        onRetry={() => void list.refetch()}
      >
        <Card padding="md">
          <ul className="grid gap-2">
            {list.data?.assessments.map((e) => (
              <li key={e._id} className="p-3 rounded border border-line-primary">
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <div>
                    <div className="font-medium">{e.title ?? e.name ?? 'Untitled'}</div>
                    <div className="text-xs text-ink-tertiary">
                      {e.description || 'No description'} · Window {new Date(e.window.opensAt).toLocaleDateString('en-IN')} → {new Date(e.window.closesAt).toLocaleDateString('en-IN')}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge tone={e.status === 'published' ? 'success' : e.status === 'draft' ? 'outline' : 'info'}>
                      {e.status}
                    </Badge>
                    <span className="text-xs text-ink-tertiary">{e.scoring.totalMarks} marks</span>
                    {e.status === 'draft' && (
                      <div className="grid gap-1" style={{ justifyItems: 'end' }}>
                        <Button
                          variant="primary"
                          onClick={() => doPublish(e._id)}
                          loading={publish.isPending}
                          title="Ready assessments can be attached to opportunity workflow stages"
                        >
                          Save as ready to attach
                        </Button>
                        <span className="helper" style={{ fontSize: 11, color: 'rgb(var(--ink-tertiary))' }}>
                          Ready assessments can be attached to opportunity workflow stages.
                        </span>
                      </div>
                    )}
                    {e.status === 'published' && (
                      <Link className="text-ink-info text-sm underline" to={`/assess/take/${e._id}`}>Preview take →</Link>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      </AsyncBoundary>

      <NewExamDrawer open={openNew} onClose={() => setOpenNew(false)} onCreated={() => setOpenNew(false)} />
    </div>
  );
}

function NewExamDrawer({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const create = useCreateExam();
  const toast = useToast();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [sectionTitle, setSectionTitle] = useState('Aptitude');
  const [totalMarks, setTotalMarks] = useState(100);
  const [passingMarks, setPassingMarks] = useState(40);
  const [opensAt, setOpensAt] = useState(new Date().toISOString().slice(0, 16));
  const [closesAt, setClosesAt] = useState(new Date(Date.now() + 7 * 24 * 3600 * 1000).toISOString().slice(0, 16));

  async function submit() {
    if (!name.trim()) { toast.show({ tone: 'warning', message: 'Name is required.' }); return; }
    try {
      const sections: ExamSectionInput[] = [{ title: sectionTitle || 'Section 1', source: 'fixed', questions: [] }];
      await create.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
        sections,
        scoring: { totalMarks, passingMarks, negativeMarking: false },
        window: { opensAt: new Date(opensAt).toISOString(), closesAt: new Date(closesAt).toISOString() },
      });
      toast.show({ message: 'Assessment created — add questions before publishing.' });
      onCreated();
      setName(''); setDescription('');
    } catch {
      toast.show({ tone: 'warning', message: 'Could not create assessment.' });
    }
  }

  return (
    <Drawer open={open} onClose={onClose} title="New assessment" size="lg">
      <div className="stack">
        <div>
          <label className="text-xs uppercase tracking-wide text-ink-tertiary">Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} className="input w-full mt-1" placeholder="Placement aptitude Round 1" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-ink-tertiary">Description</label>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="input w-full mt-1" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="grid gap-1">
            <span className="text-xs uppercase tracking-wide text-ink-tertiary">Total marks</span>
            <input type="number" value={totalMarks} onChange={(e) => setTotalMarks(Number(e.target.value))} className="input" />
          </label>
          <label className="grid gap-1">
            <span className="text-xs uppercase tracking-wide text-ink-tertiary">Passing marks</span>
            <input type="number" value={passingMarks} onChange={(e) => setPassingMarks(Number(e.target.value))} className="input" />
          </label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="grid gap-1">
            <span className="text-xs uppercase tracking-wide text-ink-tertiary">Opens</span>
            <input type="datetime-local" value={opensAt} onChange={(e) => setOpensAt(e.target.value)} className="input" />
          </label>
          <label className="grid gap-1">
            <span className="text-xs uppercase tracking-wide text-ink-tertiary">Closes</span>
            <input type="datetime-local" value={closesAt} onChange={(e) => setClosesAt(e.target.value)} className="input" />
          </label>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-ink-tertiary">First section name</label>
          <input value={sectionTitle} onChange={(e) => setSectionTitle(e.target.value)} className="input w-full mt-1" />
          <div className="text-xs text-ink-tertiary mt-1">
            You'll attach questions from the question bank after creating the assessment.
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={submit} loading={create.isPending}>Create draft</Button>
        </div>
      </div>
    </Drawer>
  );
}
